/* ==========================================================================
   Rook UI Core — Dialog Controller
   Web Component: <rk-dialog-element>
   Hydration tier: 'interaction' (external trigger pattern)
   ========================================================================== */

import { RkComponent } from '@rook/component';

function waitForExitAnimation(el, done) {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!el || reduce) {
        done();
        return () => {};
    }

    let settled = false;
    const onEnd = (e) => {
        if (e.target !== el) return;
        finish();
    };
    const finish = () => {
        if (settled) return;
        settled = true;
        el.removeEventListener('transitionend', onEnd);
        clearTimeout(timer);
        done();
    };
    const cancel = () => {
        if (settled) return;
        settled = true;
        el.removeEventListener('transitionend', onEnd);
        clearTimeout(timer);
    };
    el.addEventListener('transitionend', onEnd);
    const timer = setTimeout(finish, 500);
    return cancel;
}

class RkDialogElement extends RkComponent {
    static hydration = 'interaction';

    #cancelExitAnim = null;
    #keydownController = null;
    #triggerCleanups = [];

    attachHydrationTrigger(hydrate) {
        const dialog = this.querySelector('.rk-dialog');
        const id = this.dataset.dialogId || dialog?.id;
        if (!id) return () => {};

        const triggers = document.querySelectorAll(`[data-dialog-open="${id}"]`);
        const cleanups = [];

        triggers.forEach((trigger) => {
            const onClick = (event) => {
                event.preventDefault();
                trigger.removeEventListener('click', onClick);
                hydrate();
                this.open();
            };
            trigger.addEventListener('click', onClick);
            cleanups.push(() => trigger.removeEventListener('click', onClick));
        });

        return () => cleanups.forEach((fn) => fn());
    }

    setup() {
        this.dialog = this.querySelector('.rk-dialog');
        this.overlay = this.querySelector('.rk-dialog__overlay');
        this.content = this.querySelector('.rk-dialog__content');
        this.previousFocus = null;

        if (!this.dialog) return;

        const overlayClose = this.dataset.overlayClose !== 'false';

        const delegateMap = {
            'click [data-action="close"]': () => this.close(),
        };
        if (overlayClose && this.overlay) {
            delegateMap['click .rk-dialog__overlay'] = (event, target) => {
                if (event.target === target) this.close();
            };
        }
        this.delegate(delegateMap);

        this.#setupPersistentOpenTriggers();
    }

    teardown() {
        if (this.#cancelExitAnim) {
            this.#cancelExitAnim();
            this.#cancelExitAnim = null;
        }
        if (this.#keydownController) {
            this.#keydownController.abort();
            this.#keydownController = null;
        }
        this.#triggerCleanups.forEach((fn) => fn());
        this.#triggerCleanups = [];
    }

    #setupPersistentOpenTriggers() {
        const dialogId = this.dataset.dialogId || this.dialog?.id;
        if (!dialogId) return;

        const triggers = document.querySelectorAll(`[data-dialog-open="${dialogId}"]`);
        triggers.forEach((trigger) => {
            const onClick = (e) => {
                e.preventDefault();
                this.open();
            };
            trigger.addEventListener('click', onClick);
            this.#triggerCleanups.push(() => trigger.removeEventListener('click', onClick));
        });
    }

    isOpen() {
        return this.dialog?.classList.contains('rk-dialog--active');
    }

    open() {
        if (!this.dialog) return;

        if (this.#cancelExitAnim) {
            this.#cancelExitAnim();
            this.#cancelExitAnim = null;
        }

        if (this.isOpen() && this.dialog.dataset.state !== 'closed') return;

        this.previousFocus = document.activeElement;
        this.dialog.classList.add('rk-dialog--active');
        this.dialog.dataset.state = 'open';
        document.body.style.overflow = 'hidden';

        this.#keydownController?.abort();
        this.#keydownController = new AbortController();
        document.addEventListener('keydown', (e) => this.#onKeyDown(e), { signal: this.#keydownController.signal });

        requestAnimationFrame(() => {
            const firstFocusable = this.content?.querySelector(
                'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            );
            if (firstFocusable) {
                firstFocusable.focus();
            }
        });

        this.rkEmit('dialog:open', { id: this.dialog.id });
    }

    close() {
        if (!this.dialog || !this.isOpen()) return;
        if (this.dialog.dataset.state === 'closed') return;

        this.dialog.dataset.state = 'closed';

        const finalize = () => {
            this.#cancelExitAnim = null;
            this.dialog.classList.remove('rk-dialog--active');
            delete this.dialog.dataset.state;
            document.body.style.overflow = '';

            this.#keydownController?.abort();
            this.#keydownController = null;

            if (this.previousFocus) {
                this.previousFocus.focus();
                this.previousFocus = null;
            }

            this.rkEmit('dialog:close', { id: this.dialog.id });
        };

        this.#cancelExitAnim = waitForExitAnimation(this.content, finalize);
    }

    #onKeyDown(e) {
        if (!this.isOpen()) return;

        if (e.key === 'Escape') {
            e.preventDefault();
            this.close();
            return;
        }

        if (e.key === 'Tab') {
            this.#trapFocus(e);
        }
    }

    #trapFocus(e) {
        const focusable = this.content?.querySelectorAll(
            'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );

        if (!focusable || focusable.length === 0) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey) {
            if (document.activeElement === first) {
                e.preventDefault();
                last.focus();
            }
        } else {
            if (document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        }
    }
}

if (!customElements.get('rk-dialog-element')) {
    customElements.define('rk-dialog-element', RkDialogElement);
}
